'use client';

import { MaterialReactTable } from 'material-react-table';
import { Download, ViewList, ViewModule } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import {
  US_STATES,
  DEFAULT_NEW_LOCATION_COUNTRY,
  DEFAULT_NEW_LOCATION_SUBDIVISION,
} from '@/constants/form.constants';
import { useNavigate } from 'react-router-dom';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import {
  useCreateTomVendingLocation,
  useTomVendingLocationsList,
  tomVendingLocationIsWarehouse,
} from '@/hooks/useTomVendingLocation';

const TomsVendingLocationsPage: React.FC = () => {
  const createMutation = useCreateTomVendingLocation();
  const { data: list = [], isLoading, refetch } = useTomVendingLocationsList();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const navigate = useNavigate();
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

  const filteredData = useMemo(() => {
    let filtered = list;
    if (!showInactive) {
      filtered = filtered.filter((loc: any) => {
        const isActive =
          loc.active !== undefined && loc.active !== null ? loc.active : true;
        return isActive !== false;
      });
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter((loc: any) => {
        return (
          (loc.name || '').toLowerCase().includes(s) ||
          (loc.addressLine1 || '').toLowerCase().includes(s) ||
          (loc.city || '').toLowerCase().includes(s) ||
          (loc.subdivision || '').toLowerCase().includes(s) ||
          (loc.postalCode || '').toLowerCase().includes(s)
        );
      });
    }
    return filtered;
  }, [list, search, showInactive]);

  const getAddressString = (location: any) => {
    const { addressLine1, addressLine2, city, subdivision, postalCode, country } = location;
    return [addressLine1, addressLine2, city, subdivision, postalCode, country]
      .filter(Boolean)
      .join(', ');
  };

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    {
      accessorKey: 'name',
      header: 'Name',
      Cell: ({ row }: any) => {
        const loc = row?.original;
        const wh = tomVendingLocationIsWarehouse(loc);
        return (
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', cursor: 'pointer' }}
            onClick={() => navigate('/app/toms-vending/locations/' + loc?.id)}
          >
            <span>{loc?.name}</span>
            {wh ? <Chip label="Warehouse" size="small" variant="outlined" color="secondary" /> : null}
          </Box>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => (
        <GenericActionPicker
          row={row.original}
          actions={[
            {
              icon: GENERIC_ICON_NAME.VIEW,
              label: 'View Details',
              onClick: (r) => navigate('/app/toms-vending/locations/' + r?.id),
            },
          ]}
        />
      ),
    },
    { accessorKey: 'addressLine1', header: 'Address Line 1' },
    { accessorKey: 'city', header: 'City' },
    { accessorKey: 'subdivision', header: 'State' },
    { accessorKey: 'postalCode', header: 'Zip Code' },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations (Toms Vending)' },
  ];

  const handleExport = () => {
    if (!filteredData.length) {
      alert('No data to export.');
      return;
    }
    const rows = filteredData;
    const csvRows = [];
    const headers = Object.keys(rows[0]);
    csvRows.push(headers.join(','));
    for (const row of rows) {
      const values = headers.map((h) => JSON.stringify((row as any)[h] ?? ''));
      csvRows.push(values.join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'toms-vending-locations.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const createFields = [
    {
      groupName: 'Location Details',
      fields: [
        { label: 'Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
        { label: 'Active', name: 'active', type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 12 },
        { label: 'Address Line 1', name: 'addressLine1', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        { label: 'Address Line 2', name: 'addressLine2', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        { label: 'City', name: 'city', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        {
          label: 'State',
          name: 'subdivision',
          type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
          required: false,
          columns: 6,
          options: US_STATES,
        },
        { label: 'Zip Code', name: 'postalCode', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        { label: 'Country', name: 'country', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      ],
    },
  ];

  const renderCard = (location: any) => (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={location.id}>
      <Card
        sx={{
          cursor: 'pointer',
          height: 180,
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': { boxShadow: 4, transform: 'scale(1.03)' },
        }}
        onClick={() => navigate('/app/toms-vending/locations/' + location.id)}
      >
        <CardContent
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <div>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {location.name}
              </Typography>
              {tomVendingLocationIsWarehouse(location) ? (
                <Chip label="Warehouse" size="small" variant="outlined" color="secondary" />
              ) : null}
            </Box>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {getAddressString(location) || 'No address'}
            </Typography>
          </div>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            {location.active === false ? 'Inactive' : 'Active'}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <GenericFormModal
        visible={createModalVisible}
        onDismiss={() => setCreateModalVisible(false)}
        onSubmit={async (values: any) => {
          createMutation.mutate(values, {
            onSuccess: () => {
              refetch();
              setCreateModalVisible(false);
            },
            onError: (err) => alert('Failed to create location: ' + String(err)),
          });
        }}
        initialValues={{
          active: true,
          country: DEFAULT_NEW_LOCATION_COUNTRY,
          subdivision: DEFAULT_NEW_LOCATION_SUBDIVISION,
        }}
        fieldGroups={createFields}
        title="Add location"
      />

      <TitleBar
        breadcrumbs={breadcrumbs}
        title="Locations"
        rightContent={
          <>
            <Button
              variant="outlined"
              startIcon={<Download />}
              sx={{ textTransform: 'none' }}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              onClick={() => setCreateModalVisible(true)}
              variant="contained"
              sx={{ textTransform: 'none' }}
            >
              + Add Location
            </Button>
          </>
        }
      />

      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            placeholder="Search locations..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: '300px' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show Inactive"
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, v) => v != null && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="table">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === 'table' ? (
          <MaterialReactTable
            columns={columns}
            data={filteredData}
            manualPagination
            manualSorting
            enableGlobalFilter={false}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={filteredData.length}
            state={{ isLoading, pagination, sorting }}
            initialState={{ density: 'compact' }}
          />
        ) : (
          <Grid container spacing={2}>
            {filteredData.map(renderCard)}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default TomsVendingLocationsPage;
